/**
 * Presentation model shared by the two Voyage Radar snapshot views: the
 * "Refresh History" panel in the collections left panel (recent tail + what is
 * on the map) and the "Browse snapshots" modal (the whole archive).
 *
 * The two views are scoped differently but must never disagree, so every label
 * they render — day headers, "16h ago", LATEST — comes from here rather than
 * being re-derived per component.
 *
 * A refresh-history record is written by the API as `{ name, refreshId, count? }`
 * (see `saveSensorArtifact` in eetaskapi/utils.js). `count` only exists on
 * records written after it was added, so every consumer must treat it as
 * optional. Everything here is pure so the views stay trivially testable and so
 * the components can memoise the result — Angular re-evaluates template method
 * calls on every change-detection pass.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad2 = (n: number) => String(n).padStart(2, '0');

/** One selectable snapshot row. */
export interface VoyageHistoryRow {
  /** The raw refreshHistory record — handed straight back to display/download. */
  data: any;
  refreshId: string;
  timeMs: number;
  /** "17:24" — UTC, 24-hour. */
  time: string;
  /** "16h ago" relative to the `now` passed in. */
  ago: string;
  /** Vessel count, or null on records written before the field existed. */
  count: number | null;
  /** True for the newest refresh in the whole history. */
  isLatest: boolean;
}

/** Rows for one UTC calendar day. */
export interface VoyageHistoryGroup {
  key: string;
  /** "Today", "Yesterday" or "25 Jul 2026". */
  label: string;
  rows: VoyageHistoryRow[];
}

export interface VoyageHistoryOptions {
  /** Reference time for the relative labels and the Today/Yesterday headers. */
  now: number;
  /** Rows to keep, newest first. Null/undefined keeps all of them. */
  limit?: number | null;
  /**
   * refreshId that owns the LATEST badge. The modal renders one day at a time,
   * so without this the newest row of every day would claim to be the newest
   * row of the archive.
   */
  latestId?: string | null;
}

/**
 * Rows the panel keeps. This is a hard cap, not a "collapsed" state — the panel
 * is the recent tail and the modal is the archive, and letting the panel grow
 * into a 200-row sidebar scroll is exactly the overlap the split removes.
 */
export const VOYAGE_HISTORY_RECENT = 6;

// `VOYAGE_NEAR_MS` (a 30-minute drift threshold) lived here, paired with
// `formatVoyageDuration` below it. Both were the remains of an APPROXIMATE-MATCH
// report — "this snapshot is 3h 43m off the time you asked for" — and nothing
// had rendered either for some time: no file in `src/` referenced them, and no
// template could, since a template only reaches component members. Removed
// rather than left as a half-feature that reads like live code. Restoring it
// means restoring the render too, not just these two.

/** One usable refresh-history record with its timestamp already parsed. */
export interface VoyageSnapshotEntry {
  data: any;
  refreshId: string;
  timeMs: number;
}

function utcDayKey(d: Date): string {
  return `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
}

/** UTC day key for an instant — the join between the calendar and the day list. */
export function voyageDayKey(timeMs: number): string {
  return utcDayKey(new Date(timeMs));
}

/**
 * Newest-first, with unparseable and id-less records dropped rather than
 * rendered as "Invalid Date". Every other function here starts from this, so
 * the panel and the modal can never disagree about which records exist.
 */
export function normalizeVoyageHistory(history: any[]): VoyageSnapshotEntry[] {
  return (Array.isArray(history) ? history : [])
    .map((data) => ({
      data,
      refreshId: data?.refreshId,
      timeMs: new Date(data?.name).getTime(),
    }))
    .filter((e) => e.refreshId && !isNaN(e.timeMs))
    .sort((a, b) => b.timeMs - a.timeMs);
}

function utcDayLabel(d: Date, now: number): string {
  const today = new Date(now);
  const diffDays = Math.round(
    (Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()) -
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())) / 86400000
  );
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "16h ago" / "4m ago" / "3d ago". Clamped at zero so clock skew never reads negative. */
export function formatVoyageAgo(timeMs: number, now: number): string {
  const secs = Math.max(0, Math.round((now - timeMs) / 1000));
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

/**
 * Newest-first, grouped by UTC day, optionally truncated to `limit` rows.
 * Records with an unparseable `name` are dropped rather than rendered as
 * "Invalid Date".
 */
export function buildVoyageHistoryGroups(
  history: any[],
  opts: VoyageHistoryOptions
): VoyageHistoryGroup[] {
  const sorted = normalizeVoyageHistory(history);

  const latestId =
    opts.latestId !== undefined ? opts.latestId : sorted[0]?.data?.refreshId ?? null;
  const visible =
    opts.limit != null && opts.limit >= 0 ? sorted.slice(0, opts.limit) : sorted;

  const groups: VoyageHistoryGroup[] = [];
  let current: VoyageHistoryGroup | null = null;

  visible.forEach((entry) => {
    const d = new Date(entry.timeMs);
    const key = utcDayKey(d);

    if (!current || current.key !== key) {
      current = { key, label: utcDayLabel(d, opts.now), rows: [] };
      groups.push(current);
    }

    current.rows.push({
      data: entry.data,
      refreshId: entry.data.refreshId,
      timeMs: entry.timeMs,
      time: `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`,
      ago: formatVoyageAgo(entry.timeMs, opts.now),
      count: typeof entry.data?.count === 'number' ? entry.data.count : null,
      isLatest: entry.data.refreshId === latestId,
    });
  });

  return groups;
}

/* ── Archive helpers — the "Browse snapshots" modal ──────────────────────────
   The panel needs the recent tail; the modal needs to answer "where is there
   any data at all" before the user commits to a day. These are the extra
   projections that second question needs. */

/** What one UTC day of the archive contains — one calendar cell's worth. */
export interface VoyageDaySummary {
  key: string;
  /** UTC midnight of the day. */
  dayMs: number;
  count: number;
  /** Earliest and latest snapshot that day, for the cell's accessible name. */
  firstMs: number;
  lastMs: number;
  /** Vessel-count spread, or null when no record that day carried a count. */
  minVessels: number | null;
  maxVessels: number | null;
}

/** Extent of the archive. Null when there is nothing usable to show. */
export interface VoyageCoverage {
  minMs: number;
  maxMs: number;
  total: number;
  latestId: string;
}

/**
 * Per-UTC-day rollup, keyed by `voyageDayKey`. This is what lets the calendar
 * shade each date by how much data sits behind it, so an empty stretch is
 * visible *before* the user picks a day rather than after.
 */
export function buildVoyageDayIndex(history: any[]): Map<string, VoyageDaySummary> {
  const index = new Map<string, VoyageDaySummary>();

  // Ascending, so firstMs/lastMs fall out of the iteration order.
  const entries = normalizeVoyageHistory(history).slice().reverse();

  for (const entry of entries) {
    const d = new Date(entry.timeMs);
    const key = utcDayKey(d);
    const count = typeof entry.data?.count === 'number' ? entry.data.count : null;

    const existing = index.get(key);
    if (!existing) {
      index.set(key, {
        key,
        dayMs: Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
        count: 1,
        firstMs: entry.timeMs,
        lastMs: entry.timeMs,
        minVessels: count,
        maxVessels: count,
      });
      continue;
    }

    existing.count += 1;
    existing.lastMs = entry.timeMs;
    if (count != null) {
      existing.minVessels = existing.minVessels == null ? count : Math.min(existing.minVessels, count);
      existing.maxVessels = existing.maxVessels == null ? count : Math.max(existing.maxVessels, count);
    }
  }

  return index;
}

/** Extent and size of the archive, or null when there is nothing usable. */
export function voyageCoverage(history: any[]): VoyageCoverage | null {
  const entries = normalizeVoyageHistory(history);
  if (!entries.length) return null;
  return {
    minMs: entries[entries.length - 1].timeMs,
    maxMs: entries[0].timeMs,
    total: entries.length,
    latestId: entries[0].refreshId,
  };
}

/**
 * Four shading steps for a day cell. Deliberately coarse: the calendar answers
 * "is there data here, roughly how much", and a finer scale would imply a
 * precision the eye cannot read off a 4px bar anyway.
 */
export function voyageDensityLevel(count: number): 0 | 1 | 2 | 3 | 4 {
  if (!count) return 0;
  if (count < 5) return 1;
  if (count < 10) return 2;
  if (count < 20) return 3;
  return 4;
}

/**
 * The snapshot closest to `targetMs`, in either direction, with the distance.
 *
 * This replaces the old range-then-collapse behaviour of `filterClosestTimestamp`:
 * asking for a window and silently getting back whichever snapshot sat nearest
 * its END is what made the old date filter unpredictable. Here the target is a
 * single instant, the result is the nearest snapshot to it, and `deltaMs` is
 * surfaced so the user is told how far it moved.
 */
export function findNearestVoyageSnapshot(
  history: any[],
  targetMs: number
): { entry: VoyageSnapshotEntry; deltaMs: number } | null {
  const entries = normalizeVoyageHistory(history);
  if (!entries.length || isNaN(targetMs)) return null;

  let best = entries[0];
  let bestDelta = Math.abs(best.timeMs - targetMs);
  for (const entry of entries) {
    const delta = Math.abs(entry.timeMs - targetMs);
    if (delta < bestDelta) {
      best = entry;
      bestDelta = delta;
    }
  }

  return { entry: best, deltaMs: best.timeMs - targetMs };
}

/**
 * Step one snapshot older (`-1`) or newer (`+1`) than `refreshId`, across the
 * WHOLE archive rather than the rows currently rendered. That is what keeps the
 * panel's six-row cap safe: the user can walk around a selection made five days
 * back without reopening the modal.
 */
export function stepVoyageSnapshot(
  history: any[],
  refreshId: string | null,
  direction: -1 | 1
): VoyageSnapshotEntry | null {
  const entries = normalizeVoyageHistory(history);
  if (!entries.length) return null;
  if (!refreshId) return entries[0];

  const index = entries.findIndex((e) => e.refreshId === refreshId);
  if (index < 0) return entries[0];

  // `entries` is newest-first, so "older" walks forward through the array.
  const next = index + (direction === -1 ? 1 : -1);
  return next >= 0 && next < entries.length ? entries[next] : null;
}

// `formatVoyageDuration` ("3h 43m" / "12 min" / "8d 2h") stood here — the other
// half of the approximate-match report described above. `pad2` survives it;
// `formatVoyageTime` below is still a caller.

/** "22 Jul 2026" for headers and coverage labels. */
export function formatVoyageDate(timeMs: number): string {
  const d = new Date(timeMs);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "Wednesday 22 July 2026" — the day list's header. */
export function formatVoyageLongDate(timeMs: number): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const d = new Date(timeMs);
  return `${days[d.getUTCDay()]} ${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "14:16" UTC. */
export function formatVoyageTime(timeMs: number): string {
  const d = new Date(timeMs);
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}
